// pages/Image/EditImage.js
import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import backendURL from "../../config";
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { Button, Modal, Input, Space, Select, Dropdown, Menu } from 'antd'; // Ant Design components
import { DownOutlined } from '@ant-design/icons';

const { Option } = Select;
const { TextArea } = Input; // Import TextArea for summary

// Helper function to ensure URL has a protocol
const ensureUrlProtocol = (url) => {
  if (!url) return '';
  if (!/^https?:\/\//i.test(url) && !/^mailto:/i.test(url) && !/^tel:/i.test(url)) {
    return 'https://' + url;
  }
  return url;
};


export default function EditImage({ document, onClose, onUpdate }) {
  // State for OCR content (edited via Tiptap)
  const [pageContent, setPageContent] = useState(document?.image_content || "<p>Initial OCR Content.</p>");
  // State for summary
  const [summary, setSummary] = useState(document?.summary || ""); // << NEW: State for summary
  // State for other document data (like filename, id, etc.)
  // const [allData, setAllData] = useState(document || {}); // allData isn't directly edited here, but used in payload

  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isTableDialogOpen, setIsTableDialogOpen] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableColumns, setTableColumns] = useState(3);
  const [toolbarSaveStatus, setToolbarSaveStatus] = useState('save');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({}), Underline,
      Table.configure({ resizable: true }), TableCell, TableHeader, TableRow,
      Link.configure({
        openOnClick: false, autolink: true, linkOnPaste: true,
        HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
      }),
    ],
    content: pageContent, // Initialize Tiptap with pageContent (OCR text)
    onUpdate: ({ editor }) => {
      // Tiptap content is now directly getHTML() in handleSave, no need to setPageContent here
      // setPageContent(editor.getHTML()); 
    },
    editorProps: {
      attributes: { class: 'tiptap-editor-area p-3 form-control' },
    },
  });

  useEffect(() => {
    const initialOcrContent = document?.image_content || "<p></p>"; // Default to empty paragraph
    const initialSummary = document?.summary || "";

    // Set initial values for controlled inputs (summary)
    setSummary(initialSummary);
    
    // Set initial content for Tiptap editor (OCR text)
    if (editor && editor.isEditable) {
        const currentEditorContent = editor.getHTML();
        if (currentEditorContent !== initialOcrContent) {
            editor.commands.setContent(initialOcrContent, false);
        }
    }
    // Note: setPageContent is not strictly needed here as editor handles its content,
    // but if you rely on pageContent state for other logic, you might sync it:
    // setPageContent(initialOcrContent); 

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document, editor]); // Depend on document and editor instance

  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Enter URL:', previousUrl || '');
    if (url === null) return;
    if (url === '') { editor.chain().focus().extendMarkRange('link').unsetLink().run(); return; }
    const validatedUrl = ensureUrlProtocol(url);
    editor.chain().focus().extendMarkRange('link').setLink({ href: validatedUrl }).run();
  }, [editor]);

  const handleSave = async () => {
    if (!editor) {
      setError("Editor not ready.");
      return;
    }

    setIsSaving(true);
    setToolbarSaveStatus('saving');
    setError(null);

    try {
      const token = localStorage.getItem("access_token");
      if (!token) throw new Error("Authentication token not found.");
      
      const headers = { Authorization: `Bearer ${token}` };
      const currentOcrTextHtml = editor.getHTML(); // Get current OCR text (as HTML) from Tiptap

      if (!document?.id) throw new Error("Image ID is missing. Cannot save.");

      // Prepare the 'all_data' payload, ensuring it includes the latest summary
      // and other relevant fields from the original 'document' object.
      // The OCR text (page_content) will be passed separately and also within all_data.
      const allDataForPayload = {
        ...document, // Spread original document data
        image_content: currentOcrTextHtml, // Update with current OCR text (HTML from Tiptap)
        summary: summary, // << NEW: Include current summary from state
        // Note: filename, user_id, etc., are already part of 'document'
      };
      // Remove any client-side only properties or large objects if not needed by backend
      delete allDataForPayload.user; // Example: if 'user' object is complex and not needed

      const requestPayload = {
        id: document.id,
        page_content: currentOcrTextHtml, // This is the main content to update (OCR text)
        all_data: allDataForPayload // This carries the summary and other metadata
      };

      console.log("Saving image data with payload:", JSON.stringify(requestPayload, null, 2));

      const response = await axios.post(
        `${backendURL}/update-image-data`,
        requestPayload,
        { headers }
      );

      if (response.status === 200 && response.data.success) { // Check for success flag from backend
        console.log("Image updated successfully:", response.data.message);
        onUpdate(); // Trigger list refresh
        setToolbarSaveStatus('saved');
        setTimeout(() => {
          setToolbarSaveStatus('save');
          onClose(); 
        }, 1500);
      } else {
         throw new Error(response.data.message || `Failed to update image (Status: ${response.status})`);
      }
    } catch (err) {
      console.error("Error updating image:", err);
      const message = err.response?.data?.message || err.message || "Failed to update the image.";
      setError(message);
      setToolbarSaveStatus('save');
    } finally {
      setIsSaving(false);
    }
  };

  // ... (Tiptap table and heading handlers remain the same) ...
  const insertTableDialog = useCallback(() => setIsTableDialogOpen(true), []);
  const closeTableDialog = useCallback(() => setIsTableDialogOpen(false), []);
  const handleInsertTable = useCallback(() => {
    if (!editor) return; const rows = parseInt(tableRows, 10); const cols = parseInt(tableColumns, 10);
    if (rows > 0 && cols > 0) { editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run(); }
    setIsTableDialogOpen(false);
  }, [editor, tableRows, tableColumns]);
  const handleTableRowsChange = useCallback((e) => setTableRows(Math.max(1, parseInt(e.target.value, 10) || 1)), []);
  const handleTableColumnsChange = useCallback((e) => setTableColumns(Math.max(1, parseInt(e.target.value, 10) || 1)), []);
  const handleAddColumn = useCallback(() => editor?.chain().focus().addColumnAfter().run(), [editor]);
  const handleAddRow = useCallback(() => editor?.chain().focus().addRowAfter().run(), [editor]);
  const handleDeleteColumn = useCallback(() => editor?.chain().focus().deleteColumn().run(), [editor]);
  const handleDeleteRow = useCallback(() => editor?.chain().focus().deleteRow().run(), [editor]);
  const handleDeleteTable = useCallback(() => editor?.chain().focus().deleteTable().run(), [editor]);
  const handleHeadingChange = useCallback((value) => {
    if (!editor) return; if (value === 'paragraph') { editor.chain().focus().setParagraph().run(); }
    else { const level = parseInt(value.slice(1), 10); if (level >= 1 && level <= 6) { editor.chain().focus().toggleHeading({ level }).run();}}
  }, [editor]);

  if (!editor) {
    return <div className="p-3 text-center">Loading editor...</div>;
  }

  const tableMenu = (
    <Menu>
      <Menu.Item key="insertTable" onClick={insertTableDialog}>Insert Table</Menu.Item>
      <Menu.Item key="addColumn" onClick={handleAddColumn} disabled={!editor.can().addColumnAfter()}>Add Column After</Menu.Item>
      <Menu.Item key="addRow" onClick={handleAddRow} disabled={!editor.can().addRowAfter()}>Add Row After</Menu.Item>
      <Menu.Item key="deleteColumn" onClick={handleDeleteColumn} disabled={!editor.can().deleteColumn()}>Delete Column</Menu.Item>
      <Menu.Item key="deleteRow" onClick={handleDeleteRow} disabled={!editor.can().deleteRow()}>Delete Row</Menu.Item>
      <Menu.Divider />
      <Menu.Item key="deleteTable" onClick={handleDeleteTable} disabled={!editor.can().deleteTable()}>Delete Table</Menu.Item>
    </Menu>
  );
  const currentHeadingLevel = () => {
    for (let i = 1; i <= 6; i++) { if (editor.isActive('heading', { level: i })) return `h${i}`; } return 'paragraph';
  };

  return (
    <div className="edit-image-modal-content d-flex flex-column" style={{height: '100%', overflow:'hidden', backgroundColor: '#fff'}}>
      <div className="modal-header py-2 px-3 border-bottom">
        <h5 className="modal-title">Edit Image OCR & Summary: {document?.filename}</h5>
        <button type="button" className="btn-close" aria-label="Close" onClick={onClose} disabled={isSaving}></button>
      </div>
      
      {/* --- Tiptap Toolbar (using Ant Design components) --- */}
      <div className="tiptap-toolbar p-2 border-bottom bg-light sticky-top d-flex align-items-center flex-wrap">
          <Button size="small" className="me-1" onClick={() => editor.chain().focus().toggleBold().run()} type={editor.isActive('bold') ? 'primary' : 'default'} title="Bold"><i className="fa fa-bold"></i></Button>
          <Button size="small" className="me-1" onClick={() => editor.chain().focus().toggleItalic().run()} type={editor.isActive('italic') ? 'primary' : 'default'} title="Italic"><i className="fa fa-italic"></i></Button>
          <Button size="small" className="me-1" onClick={() => editor.chain().focus().toggleUnderline().run()} type={editor.isActive('underline') ? 'primary' : 'default'} title="Underline"><i className="fa fa-underline"></i></Button>
          <Button size="small" className="me-1" onClick={() => editor.chain().focus().toggleStrike().run()} type={editor.isActive('strike') ? 'primary' : 'default'} title="Strike"><i className="fa fa-strikethrough"></i></Button>
          <Button size="small" className="me-1" onClick={setLink} type={editor.isActive('link') ? 'primary' : 'default'} title="Link"><i className="fa fa-link"></i></Button>
          <Select size="small" value={currentHeadingLevel()} style={{ width: 120, margin: '0 8px' , zIndex: 1400 }} onChange={handleHeadingChange} disabled={!editor.can().setParagraph() && !editor.can().toggleHeading({level: 1})}>
            <Option value="paragraph">Paragraph</Option><Option value="h1">H1</Option><Option value="h2">H2</Option><Option value="h3">H3</Option>
            <Option value="h4">H4</Option><Option value="h5">H5</Option><Option value="h6">H6</Option>
          </Select>
          <Button size="small" className="me-1" onClick={() => editor.chain().focus().toggleBulletList().run()} type={editor.isActive('bulletList') ? 'primary' : 'default'} title="Bullet List"><i className="fa fa-list-ul"></i></Button>
          <Button size="small" className="me-1" onClick={() => editor.chain().focus().toggleOrderedList().run()} type={editor.isActive('orderedList') ? 'primary' : 'default'} title="Ordered List"><i className="fa fa-list-ol"></i></Button>
          <Dropdown overlay={tableMenu} trigger={['click']} overlayStyle={{ zIndex: 1500 }}>
            <Button size="small" className="me-1"><span><i className="fa fa-table" style={{ marginRight: '4px'}}></i> Table <DownOutlined /></span></Button>
          </Dropdown>
          <Button size="small" className="me-1" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo"><i className="fa fa-undo"></i></Button>
          <Button size="small" className="me-1" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo"><i className="fa fa-repeat"></i></Button>
          <Button size="small" onClick={handleSave} title="Save Changes" loading={isSaving || toolbarSaveStatus === 'saving'} 
                     icon={toolbarSaveStatus === 'saving' ? <i className="fa fa-spinner fa-spin"></i> : toolbarSaveStatus === 'saved' ? <i className="fa fa-check"></i> : <i className="fa fa-save"></i>}>
          </Button>
      </div>

      <div className="modal-body p-3" style={{flexGrow:1, overflowY:'auto'}}>
        {error && (
            <div className="alert alert-danger py-1 px-2 mb-2" role="alert" style={{fontSize: '0.85rem'}}>
                <strong>Error:</strong> {error}
            </div>
        )}
        
        {/* Editor for Image OCR Content */}
        <div className="mb-3">
          <label htmlFor="tiptap-image-ocr-content" className="form-label fw-bold">Image OCR Content (Editable):</label>
          <EditorContent editor={editor} id="tiptap-image-ocr-content" />
        </div>

        {/* ------------- NEW: Summary Input Field ------------- */}
        <div className="mb-3">
          <label htmlFor="imageSummaryEdit" className="form-label fw-bold">
            Summary:
          </label>
          <TextArea
            id="imageSummaryEdit"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="Enter a summary for the image's content (optional)"
            rows={3} // Adjust rows as needed
            disabled={isSaving}
          />
           <small className="text-muted d-block mt-1">
                A brief summary of the image's OCR content. Saved as plain text.
            </small>
        </div>
        {/* ------------- END: Summary Input Field ------------- */}
      </div>

      <div className="modal-footer py-2 px-3 border-top">
          <Button onClick={onClose} disabled={isSaving} icon={<i className="fa fa-times" style={{ marginRight: '4px' }}></i>}>
             Cancel
          </Button>
          <Button type="primary" onClick={handleSave} loading={isSaving || toolbarSaveStatus === 'saving'} 
                  icon={toolbarSaveStatus === 'saving' ? <i className="fa fa-spinner fa-spin"></i> : toolbarSaveStatus === 'saved' ? <i className="fa fa-check"></i> : <i className="fa fa-save"></i>}
                  style={{minWidth: '100px'}}>
            {toolbarSaveStatus === 'saving' ? 'Saving...' : toolbarSaveStatus === 'saved' ? 'Saved!' : 'Save'}
          </Button>
      </div>
      
      <Modal title="Insert Table" open={isTableDialogOpen} onOk={handleInsertTable} onCancel={closeTableDialog} okText="Insert" cancelText="Cancel" zIndex={1500}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input type="number" addonBefore="Rows" value={tableRows} onChange={handleTableRowsChange} min={1} />
          <Input type="number" addonBefore="Columns" value={tableColumns} onChange={handleTableColumnsChange} min={1} />
        </Space>
      </Modal>
      
      {/* Reusing styles from EditDocuments, adjust class names if needed */}
      <style jsx global>{`
        .edit-image-modal-content .tiptap-toolbar { z-index: 10; }
        .edit-image-modal-content .tiptap-editor-area {
            min-height: 150px; /* Adjust as needed */
            max-height: calc(90vh - 380px); /* Adjust based on other elements height */
            overflow-y: auto;
            border: 1px solid #ced4da;
            border-radius: 0.25rem;
        }
        .ProseMirror { padding: 0.5rem; }
        .ProseMirror p { margin-bottom: 0.5rem; line-height: 1.5; }
        .ProseMirror ul, .ProseMirror ol { padding-left: 1.5rem; margin-bottom: 0.5rem; }
        .ProseMirror a { color: #0d6efd; text-decoration: underline; cursor: pointer; }
        .ProseMirror table { border-collapse: collapse; margin: 0.5rem 0; width: 100%; }
        .ProseMirror th, .ProseMirror td { border: 1px solid #dee2e6; padding: 0.3rem 0.4rem; vertical-align: top; }
        .ProseMirror th { font-weight: bold; text-align: left; background-color: #f1f3f5; }
        .ant-select-dropdown, .ant-dropdown-menu { z-index: 1450 !important; } /* Higher than MUI Modal's z-index (usually 1300) */
        .ant-modal-root { z-index: 1500 !important; } /* For AntD's own modals like insert table */
       `}</style>
    </div>
  );
}